'use client';
import React from 'react';
import { Play, RotateCcw, Zap, ToggleLeft, ToggleRight, Loader2, CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';
import type { CircuitResult } from './circuitTypes';

interface Props {
  onTest: () => void;
  onReset: () => void;
  simulationMode: boolean;
  onToggleSimulation: () => void;
  wireCount: number;
  componentCount: number;
  isLoading: boolean;
  circuitResult: CircuitResult | null;
}

const statusConfig = {
  idle: { label: 'Not tested', color: 'text-slate-500', bg: 'bg-slate-100' },
  correct: { label: 'Correct & Safe', color: 'text-green-700', bg: 'bg-green-50' },
  incorrect: { label: 'Incorrect', color: 'text-amber-700', bg: 'bg-amber-50' },
  unsafe: { label: 'Unsafe', color: 'text-red-700', bg: 'bg-red-50' },
  'short-circuit': { label: 'Short Circuit!', color: 'text-pink-700', bg: 'bg-pink-50' },
  incomplete: { label: 'Incomplete', color: 'text-slate-600', bg: 'bg-slate-100' },
};

export default function SimulatorToolbar({
  onTest, onReset, simulationMode, onToggleSimulation,
  wireCount, componentCount, isLoading, circuitResult,
}: Props) {
  const status = circuitResult?.status ?? 'idle';
  const cfg = statusConfig[status];

  return (
    <div className="bg-white border-b border-border px-4 py-2.5 flex items-center gap-3 flex-wrap">
      {/* Test button */}
      <button
        onClick={onTest}
        disabled={isLoading || componentCount === 0}
        className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-95"
      >
        {isLoading ? (
          <Loader2 size={15} className="animate-spin" />
        ) : (
          <Play size={15} />
        )}
        {isLoading ? 'Testing...' : 'Test Circuit'}
      </button>

      {/* Simulation mode toggle */}
      <button
        onClick={onToggleSimulation}
        className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-all duration-150 active:scale-95
          ${simulationMode
            ? 'bg-amber-50 border-amber-300 text-amber-700' :'bg-white border-border text-muted-foreground hover:bg-muted'
          }`}
      >
        {simulationMode ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
        Simulation Mode
      </button>

      {/* Reset */}
      <button
        onClick={onReset}
        className="flex items-center gap-2 px-3 py-2 bg-white border border-border text-muted-foreground text-sm font-medium rounded-lg hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-all duration-150 active:scale-95"
      >
        <RotateCcw size={15} />
        Reset
      </button>

      <div className="flex-1" />

      {/* Stats */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground font-mono">
        <span>{componentCount} components</span>
        <span>{wireCount} wires</span>
      </div>

      {/* Status pill */}
      {circuitResult && (
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${cfg.bg} ${cfg.color} border border-current/20`}>
          {status === 'correct' && <CheckCircle size={13} />}
          {status === 'incorrect' && <AlertTriangle size={13} />}
          {status === 'unsafe' && <XCircle size={13} />}
          {status === 'short-circuit' && <Zap size={13} />}
          {status === 'incomplete' && <Info size={13} />}
          {cfg.label}
        </div>
      )}
    </div>
  );
}